/**
 * Cliente da API administrativa.
 *
 * Mesma regra do lado da participante: o cookie de sessão viaja sozinho e a
 * tela nunca lê o seu valor. O token CSRF é lido de um cookie separado e
 * legível (`nev_admin_csrf`) e ecoado automaticamente em toda escrita — é o
 * único canal pelo qual esse valor chega ao navegador, já que o contrato
 * oficial não reserva campo para ele em nenhuma resposta JSON. A prova de
 * reautenticação, quando existe, fica apenas em memória durante os cinco
 * minutos de validade — nunca em `localStorage`, nunca em URL.
 */

const BASE = '/api/v1';
const CSRF_COOKIE_NAME = 'nev_admin_csrf';

function readCsrfCookie(): string | undefined {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${CSRF_COOKIE_NAME}=`));
  return match?.slice(CSRF_COOKIE_NAME.length + 1);
}

export interface FieldError {
  readonly field: string;
  readonly code: string;
}

export interface ApiProblem {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly fields?: readonly FieldError[];
  readonly retryAfterSeconds?: number;
}

export type ApiResult<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly problem: ApiProblem };

async function request<T>(
  path: string,
  init: RequestInit & {
    withCsrf?: boolean;
    idempotencyKey?: string;
    reauthToken?: string;
  } = {},
): Promise<ApiResult<T>> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');

  if (init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (init.withCsrf === true) {
    const csrfToken = readCsrfCookie();
    if (csrfToken !== undefined) {
      headers.set('X-CSRF-Token', csrfToken);
    }
  }
  if (init.idempotencyKey !== undefined) {
    headers.set('Idempotency-Key', init.idempotencyKey);
  }
  if (init.reauthToken !== undefined) {
    headers.set('X-Reauthentication-Token', init.reauthToken);
  }

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'same-origin' });
  } catch {
    return {
      ok: false,
      problem: { status: 0, code: 'NETWORK_ERROR', title: 'Não foi possível conectar ao serviço.' },
    };
  }

  if (response.status === 204) {
    return { ok: true, value: undefined as T };
  }

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const problem = (body ?? {}) as Partial<ApiProblem>;
    const retryAfter = response.headers.get('Retry-After');

    return {
      ok: false,
      problem: {
        status: response.status,
        code: problem.code ?? 'UNKNOWN',
        title: problem.title ?? 'Não foi possível concluir a operação.',
        ...(problem.fields !== undefined ? { fields: problem.fields } : {}),
        ...(retryAfter !== null ? { retryAfterSeconds: Number(retryAfter) } : {}),
      },
    };
  }

  return { ok: true, value: body as T };
}

export interface TrustedDevice {
  readonly id: string;
  readonly label: string;
  readonly created_at: string;
  readonly last_used_at: string;
  readonly expires_at: string;
  readonly current: boolean;
}

export interface StepUpResult {
  readonly session_established: boolean;
  readonly reauthentication_token: string | null;
  readonly expires_at: string;
}

export const adminApi = {
  login(input: {
    email: string;
    password: string;
    turnstileToken: string;
  }): Promise<ApiResult<{ challenge_id: string; expires_at: string }>> {
    return request('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email: input.email,
        password: input.password,
        turnstile_token: input.turnstileToken,
      }),
    });
  },

  requestStepUp(input: {
    purpose: string;
  }): Promise<ApiResult<{ accepted: boolean; challenge_id: string }>> {
    return request('/admin/auth/step-up/request', {
      method: 'POST',
      withCsrf: true,
      body: JSON.stringify({ purpose: input.purpose }),
    });
  },

  verifyStepUp(input: {
    challengeId: string;
    code: string;
    purpose: string;
  }): Promise<ApiResult<StepUpResult>> {
    return request('/admin/auth/step-up/verify', {
      method: 'POST',
      withCsrf: true,
      body: JSON.stringify({
        challenge_id: input.challengeId,
        code: input.code,
        purpose: input.purpose,
      }),
    });
  },

  listDevices(): Promise<ApiResult<{ items: TrustedDevice[] }>> {
    return request('/admin/auth/devices', { method: 'GET' });
  },

  revokeDevice(input: {
    deviceId: string;
    reauthToken: string;
    idempotencyKey: string;
  }): Promise<ApiResult<{ job_id: string }>> {
    return request(`/admin/auth/devices/${input.deviceId}`, {
      method: 'DELETE',
      withCsrf: true,
      reauthToken: input.reauthToken,
      idempotencyKey: input.idempotencyKey,
    });
  },

  revokeAllDevices(input: {
    reauthToken: string;
    idempotencyKey: string;
  }): Promise<ApiResult<{ job_id: string }>> {
    return request('/admin/auth/devices', {
      method: 'DELETE',
      withCsrf: true,
      reauthToken: input.reauthToken,
      idempotencyKey: input.idempotencyKey,
    });
  },

  logout(): Promise<ApiResult<void>> {
    return request('/admin/auth/logout', { method: 'POST', withCsrf: true });
  },
};
